import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface AttachmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (attachment: {
    title: string;
    description: string;
    imageFile: File | null;
    imageUrl?: string;
  }) => void;
  initialData?: {
    title: string;
    description: string;
    imageUrl?: string;
  } | null;
}

const AttachmentModal: React.FC<AttachmentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData
}) => {
  const { darkMode } = useTheme();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [error, setError] = useState('');
  const [isQuillReady, setIsQuillReady] = useState(false);
  const quillRef = useRef<any>(null);

  useEffect(() => {
    if (isOpen) {
      // Delay mounting ReactQuill untuk menghindari findDOMNode issue
      const timer = setTimeout(() => {
        setIsQuillReady(true);
      }, 100);
      return () => {
        clearTimeout(timer);
        setIsQuillReady(false);
      };
    } else {
      setIsQuillReady(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title);
      setDescription(initialData.description);
      setImagePreview(initialData.imageUrl || '');
    } else {
      setTitle('');
      setDescription('');
      setImagePreview('');
      setImageFile(null);
    }
    setError('');
  }, [initialData, isOpen]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('File harus berupa gambar');
      return;
    }

    // Validate file size (2.5MB = 2.5 * 1024 * 1024 bytes)
    const maxSize = 2.5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Ukuran file maksimal 2.5MB');
      return;
    }

    setError('');
    setImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    if (!title.trim()) {
      setError('Title wajib diisi');
      return;
    }

    if (!imageFile && !imagePreview) {
      setError('Gambar wajib diupload');
      return;
    }

    onSave({
      title: title.trim(),
      description,
      imageFile,
      imageUrl: imagePreview
    });

    // Reset form
    setTitle('');
    setDescription('');
    setImageFile(null);
    setImagePreview('');
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className={`w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`sticky top-0 z-10 px-6 py-4 border-b ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {initialData ? 'Edit Lampiran' : 'Tambah Lampiran'}
            </h2>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                darkMode
                  ? 'hover:bg-gray-700 text-gray-400 hover:text-white'
                  : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="fa-solid fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {error && (
            <div className={`px-4 py-3 rounded-lg border ${
              darkMode
                ? 'bg-red-900/30 border-red-800 text-red-300'
                : 'bg-red-50 border-red-200 text-red-700'
            }`}>
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Title Lampiran <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Lampiran 1"
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                darkMode
                  ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                  : 'border-gray-300'
              }`}
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Upload Gambar <span className="text-red-500">*</span>
              <span className={`ml-2 text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                (Max 2.5MB)
              </span>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                darkMode
                  ? 'bg-gray-900 border-gray-700 text-white'
                  : 'border-gray-300'
              }`}
            />
            {imagePreview && (
              <div className="mt-4">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-w-full h-auto max-h-96 rounded-lg border border-gray-300"
                />
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Deskripsi / Komentar
            </label>
            <div>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Masukkan deskripsi atau komentar untuk lampiran ini..."
                rows={8}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-y ${
                  darkMode
                    ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                    : 'border-gray-300'
                }`}
              />
              <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                💡 Tip: Anda dapat menggunakan HTML untuk formatting (contoh: &lt;b&gt;teks tebal&lt;/b&gt;, &lt;i&gt;teks miring&lt;/i&gt;, &lt;ul&gt;&lt;li&gt;list&lt;/li&gt;&lt;/ul&gt;)
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 px-6 py-4 border-t ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className={`px-4 py-2 border rounded-lg font-medium transition-colors ${
                darkMode
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Batal
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 transition-colors"
            >
              {initialData ? 'Update' : 'Simpan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachmentModal;

